import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getSortFields } from 'apps/auth/components/form-multi/utils';
import KycFields from 'apps/auth/components/kyc/KycFields';
import KycGuideScreen from 'apps/auth/components/kyc/KycGuideScreen';
import LuluKycSDK from 'apps/auth/components/send/Lulu/LuluKycSDK';
import BasePage from 'apps/auth/components/utils/BasePage';
import {
    KYC_FAILED_SCREEN,
    KYC_FAILED_VERIFYING_SCREEN,
    KYC_FIELDS_SCREEN,
    KYC_GUIDE_SCREEN,
    KYC_INTRO_SCREEN,
    KYC_VERIFYING_SCREEN,
    LULU_KYC_SDK,
} from 'apps/auth/constants/ComponentNames';
import { KYC_EMIRATES_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';

const HeaderText = ({ emphasizedText, headerText }) => {
    return (
        <>
            <Text variant="span" color="emphasized">
                {emphasizedText}
            </Text>
            <br />
            {headerText}
        </>
    );
};
const sliceMap = {
    1: {
        start: 0,
        end: 2,
    },
    2: {
        start: 2,
        end: 5,
    },
    3: {
        start: 5,
        end: 7,
    },
};

const LuluKycPage = () => {
    const messages = usePageMessages();
    const extra = useExtra();
    const form = useForm(KYC_EMIRATES_FORM_NAME);
    const dispatch = useDispatch();
    const [currentScreen, setCurrentScreen] = useState({
        screen: KYC_FIELDS_SCREEN,
        message: '',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const fields = useMemo(() => form?.fields ?? {}, [form?.fields]);
    const slicedFields = useMemo(() => {
        const { start, end } = sliceMap[currentPage];
        return getSortFields(fields).slice(start, end);
    }, [currentPage, fields]);
    const { screen, message } = currentScreen;

    const titleMap = {
        1: { title: messages.kycTitleYourself, subTitle: '' },
        2: { title: messages.kycTitleAdress, subTitle: messages.kycSubTitle },
        3: { title: messages.kycTitleHabits, subTitle: '' },
    };

    const onClickIcon = (e) => {
        if (screen === KYC_GUIDE_SCREEN) {
            e.preventDefault();
            setCurrentScreen({
                ...currentScreen,
                screen: KYC_INTRO_SCREEN,
            });
        } else if (screen === KYC_FIELDS_SCREEN && currentPage !== 1) {
            e.preventDefault();
            setCurrentPage((prevState) => prevState - 1);
        }
    };
    const getBackUrl = () => {
        if (screen === KYC_FIELDS_SCREEN || screen === KYC_GUIDE_SCREEN) {
            return AUTH_SEND_MAIN_LINK;
        }
        return '';
    };

    const successFuncUpdateProfile = () => {
        toggleModalWithTimer(
            true,
            messages.profileUpdated,
        )(dispatch).then(() => {
            setCurrentScreen((prevScreen) => {
                return {
                    ...prevScreen,
                    screen: KYC_INTRO_SCREEN,
                };
            });
        });
    };

    const onClickHandler = () => {
        if (currentPage !== 3) {
            setCurrentPage((prevPage) => prevPage + 1);
        } else {
            successFuncUpdateProfile();
        }
    };

    const componentMap = {
        [KYC_INTRO_SCREEN]: (
            <InfoPage
                iconName="id-card-80"
                iconColor="primary"
                headerText={<HeaderText emphasizedText={messages.luluToLevelPurpleText} headerText={messages.luluToLevelHeaderText} />}
                secondaryText={messages.luluToLevelSecondaryText}
                primaryButtonCaption={messages.continueButton}
                topButtonCaption={messages.notNowButton}
                topButtonType="link"
                topButtonLink={AUTH_SEND_MAIN_LINK}
                primaryButtonOnClick={() =>
                    setCurrentScreen({
                        ...currentScreen,
                        screen: KYC_GUIDE_SCREEN,
                    })
                }
            />
        ),
        [KYC_GUIDE_SCREEN]: (
            <KycGuideScreen
                primaryButtonOnClick={(e) => {
                    e.preventDefault();
                    setCurrentScreen({
                        ...currentScreen,
                        screen: LULU_KYC_SDK,
                    });
                }}
                title={messages.luluGuideTitle}
                subTitle={messages.luluGuideSubTitle}
                caption={messages.readyButton}
                chatTitle={messages.chatLinkTitle}
                items={extra.luluKycItems}
            />
        ),
        [KYC_VERIFYING_SCREEN]: (
            <InfoPage
                iconName="id-card-search-80"
                iconColor="primary"
                primaryButtonType="link"
                headerText={messages.verifyingIdentity}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.resultSubtitle} />}
                primaryButtonCaption={messages.gotIt}
                primaryButtonLink={AUTH_SEND_MAIN_LINK}
            />
        ),
        [KYC_FAILED_VERIFYING_SCREEN]: (
            <InfoPage
                iconName="id-card-cross-80"
                iconColor="primary"
                headerText={messages.errorOnfidoTitle}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.errorOnfidoSubtitle} />}
                topButtonCaption={messages.notNowButton}
                topButtonType="link"
                topButtonLink={AUTH_SEND_MAIN_LINK}
                buttonRowCaption={messages.chatLinkTitle}
                buttonRowOnClick={(e) => {
                    e.preventDefault(e);
                    toggleChat(true)(dispatch);
                }}
                primaryButtonCaption={messages.agreeButton}
                primaryButtonOnClick={() =>
                    setCurrentScreen({
                        ...currentScreen,
                        screen: KYC_INTRO_SCREEN,
                    })
                }
            />
        ),
        [KYC_FIELDS_SCREEN]: (
            <KycFields
                title={titleMap[currentPage].title}
                subtitle={titleMap[currentPage].subTitle}
                caption={messages.continueButton}
                toCountry={extra?.toCountry}
                formName={KYC_EMIRATES_FORM_NAME}
                fields={slicedFields}
                onClickHandler={onClickHandler}
            />
        ),
        [KYC_FAILED_SCREEN]: (
            <InfoPage
                animatedIconFail
                primaryButtonType="link"
                headerText={message || messages.OhWentWrong}
                primaryButtonCaption={messages.goBack}
                primaryButtonLink={AUTH_SEND_MAIN_LINK}
            />
        ),
        [LULU_KYC_SDK]: (
            <LuluKycSDK
                luluKycEncodedHtml={extra?.luluKycData?.encodedHtml}
                kycRequestId={extra?.luluKycData?.kycRequestId}
                setCurrentScreen={setCurrentScreen}
                documentSubmitted={messages.documentSubmitted}
                fields={fields}
            />
        ),
    };
    return (
        <CommonPage isTagA backUrl={getBackUrl()} onClickIcon={onClickIcon} showChat={false}>
            {screen ? componentMap[screen] : null}
        </CommonPage>
    );
};

export default BasePage(LuluKycPage);
